import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Video, VideoOff, Mic, MicOff, Phone, PhoneOff, Image as ImageIcon, Languages, MessageSquare, X } from 'lucide-react';
import SimplePeer from 'simple-peer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'tr', name: 'Turkish' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'pl', name: 'Polish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'th', name: 'Thai' },
  { code: 'sv', name: 'Swedish' }
];

const VideoCall = ({ conversation, ws, user, onClose }) => {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});  // For group calls
  const [peer, setPeer] = useState(null);
  const [peers, setPeers] = useState({});  // For group calls
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [translation, setTranslation] = useState('');
  const [sharedImage, setSharedImage] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [callId, setCallId] = useState(null);
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const roomId = conversation.id;
  const fileInputRef = useRef(null);

  useEffect(() => {
    initializeMedia();
    setupWebSocketListeners();
    startCall(); // Auto-start call when component mounts

    const durationInterval = setInterval(() => {
      if (isCallActive) {
        setCallDuration((prev) => prev + 1);
      }
    }, 1000);

    return () => {
      cleanup();
      clearInterval(durationInterval);
    };
  }, []);

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      alert('Please allow camera and microphone access');
    }
  };

  const setupWebSocketListeners = () => {
    if (!ws) return;

    const handleMessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'video_signal') {
        handleVideoSignal(data);
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  };

  const handleVideoSignal = (data) => {
    if (data.signal_type === 'offer') {
      createAnswer(data.data, data.from_user_id);
    } else if (data.signal_type === 'answer') {
      peer?.signal(data.data);
    } else if (data.signal_type === 'ice-candidate') {
      peer?.signal(data.data);
    } else if (data.signal_type === 'user_joined') {
      // Another user joined
    } else if (data.signal_type === 'user_left') {
      handleCallEnd();
    } else if (data.signal_type === 'image_share') {
      setSharedImage(data.data.imageUrl);
      setTimeout(() => setSharedImage(null), 10000);
    }
  };

  const startCall = async () => {
    if (!localStream) return;

    try {
      // Call backend to create call record
      const participantIds = conversation.participants
        .filter(p => p.id !== user.id)
        .map(p => p.id);
      
      const response = await axios.post(`${API}/calls/start`, {
        call_type: 'video',
        participant_ids: participantIds
      });

      setCallId(response.data.call_id);
      setIsCallActive(true);

      const newPeer = new SimplePeer({
        initiator: true,
        trickle: false,
        stream: localStream
      });

      newPeer.on('signal', (signal) => {
        sendSignal('offer', signal);
      });

      newPeer.on('stream', (stream) => {
        setRemoteStream(stream);
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
        }
      });

      setPeer(newPeer);

      ws.send(JSON.stringify({
        type: 'video_signal',
        room_id: roomId,
        signal_type: 'join'
      }));
    } catch (error) {
      console.error('Error starting call:', error);
    }
  };

  const createAnswer = (offer, fromUserId) => {
    if (!localStream) return;

    const newPeer = new SimplePeer({
      initiator: false,
      trickle: false,
      stream: localStream
    });

    newPeer.on('signal', (signal) => {
      sendSignal('answer', signal, fromUserId);
    });

    newPeer.on('stream', (stream) => {
      setRemoteStream(stream);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
      }
    });

    newPeer.signal(offer);
    setPeer(newPeer);
    setIsCallActive(true);
  };

  const sendSignal = (signalType, data, targetUserId = null) => {
    ws.send(JSON.stringify({
      type: 'video_signal',
      room_id: roomId,
      signal_type: signalType,
      data: data,
      target_user_id: targetUserId
    }));
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing, return to camera
      if (screenStream) {
        screenStream.getTracks().forEach((track) => track.stop());
      }
      
      // Switch back to camera
      if (peer && localStream) {
        const videoTrack = localStream.getVideoTracks()[0];
        const sender = peer._pc.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(videoTrack);
        }
      }
      
      setScreenStream(null);
      setIsScreenSharing(false);
    } else {
      // Start screen sharing
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always' },
          audio: false
        });
        
        setScreenStream(stream);
        setIsScreenSharing(true);
        
        // Replace video track with screen track
        if (peer) {
          const screenTrack = stream.getVideoTracks()[0];
          const sender = peer._pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
          
          // Stop sharing when user stops from browser UI
          screenTrack.onended = () => {
            toggleScreenShare();
          };
        }
      } catch (error) {
        console.error('Error starting screen share:', error);
        alert('Could not start screen sharing. Please allow permission.');
      }
    }
  };

  const handleCallEnd = async () => {
    if (callId) {
      try {
        await axios.post(`${API}/calls/${callId}/end`, { duration: callDuration });
      } catch (error) {
        console.error('Error ending call:', error);
      }
    }
    cleanup();
    onClose();
  };

  const cleanup = () => {
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }
    if (peer) {
      peer.destroy();
    }
    setIsCallActive(false);
    
    ws?.send(JSON.stringify({
      type: 'video_signal',
      room_id: roomId,
      signal_type: 'leave'
    }));
  };

  const handleImageShare = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      sendSignal('image_share', { imageUrl: response.data.url });
      setSharedImage(response.data.url);
      setTimeout(() => setSharedImage(null), 10000);
    } catch (error) {
      console.error('Error sharing image:', error);
    }
  };

  const handleTranslate = async (text) => {
    try {
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: selectedLanguage
      });
      setTranslation(response.data.translated_text);
    } catch (error) {
      console.error('Error translating:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black z-50 flex flex-col"
      style={{ pointerEvents: 'auto' }}
    >
      {/* Remote Video (Main) */}
      <div className="flex-1 relative">
        {remoteStream ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: '#1a1a1a' }}>
            <div className="text-center">
              <Video className="w-16 h-16 text-white/50 mx-auto mb-4" />
              <p className="text-white/70">Waiting for other participant...</p>
              {!isCallActive && (
                <button
                  data-testid="start-call-button"
                  onClick={startCall}
                  className="mt-4 px-6 py-3 bg-green-500 text-white rounded-full font-semibold hover:bg-green-600"
                >
                  Start Call
                </button>
              )}
            </div>
          </div>
        )}

        {/* Local Video (Picture-in-Picture) */}
        <div className="absolute top-4 right-4 w-48 h-36 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {!isVideoEnabled && (
            <div className="absolute inset-0 bg-black flex items-center justify-center">
              <VideoOff className="w-8 h-8 text-white/70" />
            </div>
          )}
        </div>

        {/* Shared Image Overlay */}
        {sharedImage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute inset-0 flex items-center justify-center bg-black/80 p-8"
          >
            <img src={sharedImage} alt="Shared" className="max-w-full max-h-full rounded-xl shadow-2xl" />
          </motion.div>
        )}

        {/* Translation Overlay */}
        {translationEnabled && (translation || transcription) && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="absolute bottom-24 left-1/2 transform -translate-x-1/2 max-w-2xl w-full px-4"
          >
            <div className="bg-black/80 backdrop-blur-md rounded-2xl p-4 text-white">
              {transcription && (
                <div className="mb-2">
                  <p className="text-xs text-white/60 mb-1">Original:</p>
                  <p className="text-sm">{transcription}</p>
                </div>
              )}
              {translation && (
                <div>
                  <p className="text-xs text-white/60 mb-1">Translation ({LANGUAGES.find(l => l.code === selectedLanguage)?.name}):</p>
                  <p className="text-sm font-medium">{translation}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Language Selector */}
        {showLanguageSelect && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-20 right-4 bg-white rounded-2xl shadow-2xl p-4 max-h-96 overflow-y-auto"
          >
            <p className="text-sm font-semibold mb-3" style={{ color: '#111111' }}>Select Target Language</p>
            <div className="space-y-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setSelectedLanguage(lang.code);
                    setShowLanguageSelect(false);
                  }}
                  className={`w-full px-3 py-2 rounded-lg text-left text-sm hover:bg-black/5 ${
                    selectedLanguage === lang.code ? 'bg-[#3B82F6] text-white' : ''
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Controls */}
      <div className="p-6 bg-gradient-to-t from-black/90 to-transparent">
        <div className="flex items-center justify-center gap-4 mb-4">
          {/* Video Toggle */}
          <button
            data-testid="toggle-video-button"
            onClick={toggleVideo}
            className={`p-4 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform ${
              isVideoEnabled ? 'bg-white/20' : 'bg-red-500'
            }`}
          >
            {isVideoEnabled ? (
              <Video className="w-6 h-6 text-white" />
            ) : (
              <VideoOff className="w-6 h-6 text-white" />
            )}
          </button>

          {/* Audio Toggle */}
          <button
            data-testid="toggle-audio-button"
            onClick={toggleAudio}
            className={`p-4 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform ${
              isAudioEnabled ? 'bg-white/20' : 'bg-red-500'
            }`}
          >
            {isAudioEnabled ? (
              <Mic className="w-6 h-6 text-white" />
            ) : (
              <MicOff className="w-6 h-6 text-white" />
            )}
          </button>

          {/* End Call */}
          <button
            data-testid="end-call-button"
            onClick={handleCallEnd}
            className="p-4 rounded-full bg-red-500 shadow-lg hover:scale-110 active:scale-95 transition-transform"
          >
            <PhoneOff className="w-6 h-6 text-white" />
          </button>

          {/* Screen Share */}
          <button
            data-testid="toggle-screen-share-button"
            onClick={toggleScreenShare}
            className={`p-4 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform ${
              isScreenSharing ? 'bg-[#3B82F6]' : 'bg-white/20'
            }`}
            title={isScreenSharing ? 'Stop Sharing' : 'Share Screen'}
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </button>

          {/* Translation Toggle */}
          <button
            data-testid="toggle-translation-button"
            onClick={() => setTranslationEnabled(!translationEnabled)}
            className={`p-4 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform ${
              translationEnabled ? 'bg-[#3B82F6]' : 'bg-white/20'
            }`}
          >
            <Languages className="w-6 h-6 text-white" />
          </button>

          {/* Language Select */}
          <button
            data-testid="select-language-button"
            onClick={() => setShowLanguageSelect(!showLanguageSelect)}
            className="p-4 rounded-full bg-white/20 shadow-lg hover:scale-110 active:scale-95 transition-transform"
          >
            <MessageSquare className="w-6 h-6 text-white" />
          </button>

          {/* Share Image */}
          <button
            data-testid="share-image-button"
            onClick={() => fileInputRef.current?.click()}
            className="p-4 rounded-full bg-white/20 shadow-lg hover:scale-110 active:scale-95 transition-transform"
          >
            <ImageIcon className="w-6 h-6 text-white" />
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageShare}
            className="hidden"
          />
        </div>

        <div className="text-center">
          <p className="text-white/70 text-sm">
            {isCallActive ? (
              <>
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2" />
                {Math.floor(callDuration / 60)}:{(callDuration % 60).toString().padStart(2, '0')}
              </>
            ) : (
              'Ready to call'
            )}
            {isScreenSharing && ' • Sharing Screen'}
            {translationEnabled && ` • Translation: ${LANGUAGES.find(l => l.code === selectedLanguage)?.name}`}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default VideoCall;
